import React, { useEffect, useRef, useState } from "react";
import { Spin } from "antd";
import FolderDetailSearchForm from "./FolderDetailSearchForm";
import Modal from "@/common/elements/Modal";
import styled from "styled-components";
import FolderFullDetails from "../BookingsDetails/JacketDetails/FolderFullDetails";
import FolderDetailSearchActions from "./FolderDetailSearchActions";
import Button from "@/common/elements/Button";
import {
  isEmptyObject,
  isNonEmptyVariable,
  objectToArray,
} from "@/util/common_function";
import { useIrisDevice } from "@/hooks/useIrisDevice";
import { useIrisSearch } from "@/hooks/useIrisSearch";
import SearchIrisDetailsPopup from "@/components/InmateDetails/SearchIrisDetailsPopup";
import useDialog from "@/hooks/useDialog";

const StyledActions = styled.div`
  position: sticky;
  top: 0px;
  background: #fff;
  padding: 0px 15px 10px 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 9999;
`;

// Matches ASP's JacketSupport.vbs/CopyInformationFromJacketAddresses,
// which prompts "Does <Full_Name> still live at <Current_Physical_Address>?"
// right after "Select Current Folder" — Full_Name there is First Middle Last.
const buildFullName = (bio) =>
  [bio?.fName, bio?.mName, bio?.lName].filter(Boolean).join(" ");

const buildCurrentPhysicalAddress = (bio) => {
  const address = bio?.bioAddresses
    ?.flatMap((entry) => entry?.addresses ?? [])
    ?.find((addr) => addr?.addressType === "Physical");
  if (!address) return "";
  return [
    [address?.addressStreetNumber, address?.addressStreet1, address?.addressStreet2]
      .filter(Boolean)
      .join(" "),
    [address?.addressCity, address?.addressState].filter(Boolean).join(", "),
    address?.addressZip,
  ]
    .filter(Boolean)
    .join("  ");
};

const FolderDetailSearchModal = ({
  show,
  onHide,
  onCloseClick,
  onSaveClick,
  initialValues = {},
  showIrisSearch = false,
  // Data Importer's "New Warrant" flow (DefendantsDetailsForm.jsx) wants the
  // search to run immediately with the pre-filled identity, same as the
  // full-page Folder Search screen it replaced (which auto-searched via
  // FolderSearchContext's irisFilter). Other callers (e.g. Associates/Create
  // Booking's lName/fName prefill) still want the fields populated but no
  // search run until the user clicks Search — see the comment below — so
  // this defaults off rather than changing that shared behavior for everyone.
  autoSearchOnOpen = false,
  // ASP's "Does <name> still live at <address>?" confirmation, asked right
  // after "Select Current Folder" for every jacket-select flow. Opt-in here
  // rather than always-on so it doesn't change behavior for this shared
  // modal's other 10+ existing callers without them asking for it.
  verifyAddressOnSelect = false,
  // Booking Intake's Create Booking flow: same "already in jail" block the
  // full-page Folder Search shows for the Bookings/Booking Intake modules.
  // Opt-in so this shared modal's other callers are unaffected.
  blockIfInJail = false,
}) => {
  const { showDialog } = useDialog();
  const searchFormRef = useRef();
  // Local state to hold the currently selected folder from the search table.
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isDisabledCurrentJacket, setisDisabledCurrentJacket] = useState(true);
  // Search Iris should only be available before a search has been run.
  const [hasSearched, setHasSearched] = useState(false);
  const { irisDeviceConnected } = useIrisDevice();

  // When Iris resolves to a person (local or national match), pre-fill the
  // name fields and re-run the search so the matching folder(s) show up in
  // the table below — the user still picks "Select Current Folder" or
  // "Create New Folder" same as a manual search.
  const handleIrisResolved = (irisRecord) => {
    searchFormRef.current?.setValues({
      ...searchFormRef.current?.values,
      lName: irisRecord?.lastName || "",
      fName: irisRecord?.firstName || "",
    });
    setTimeout(() => handleSearch(), 300);
  };

  const {
    irisLoading,
    irisSearchData,
    irisSearchDetailsPopupOpen,
    setisSearchIrisDetailsPopupOpen,
    openIrisSearch,
    returnFromDetailsPopup,
  } = useIrisSearch({ onResolved: handleIrisResolved });

  // Modal opens with pre-filled bio data (e.g. lName/fName from Create
  // Booking) via FolderDetailSearchForm's initialValues — the fields are
  // populated but no search is run until the user clicks Search, unless the
  // caller opted into autoSearchOnOpen above. Same setTimeout-after-setValues
  // pattern handleIrisResolved uses above — the ref's Formik values need a
  // tick to settle after initialValues populates them on mount before
  // handleSubmit reads them.
  useEffect(() => {
    if (show) {
      setHasSearched(false);
      if (autoSearchOnOpen && !isEmptyObject(initialValues)) {
        setTimeout(() => handleSearch(), 300);
      }
    }
  }, [show]);

  const handleModalHide = () => {
    if (onCloseClick) onCloseClick();
    if (onHide) onHide();
    setSelectedFolder(null);
  };

  // Call Formik's submit method via the ref.
  const handleSearch = () => {
    // A folder selected by an earlier search must not leave "Select Current
    // Folder" enabled for whatever this new search returns (including zero
    // results) — nothing else clears isDisabledCurrentJacket/selectedFolder
    // between searches otherwise.
    setSelectedFolder(null);
    setisDisabledCurrentJacket(true);
    if (searchFormRef.current) {
      searchFormRef.current.handleSubmit();
    }
    setHasSearched(true);
  };

  // Call Formik's reset method via the ref.
  const handleReset = () => {
    if (searchFormRef.current) {
      searchFormRef.current.resetForm();
      setSelectedFolder(null);
    }

    setisDisabledCurrentJacket(() => true);
    setHasSearched(false);
  };

  const onCreateNewJacket = () => {
    const filter = objectToArray(searchFormRef.current, "filterKey", "value");

    if (isNonEmptyVariable(filter) && filter?.length) {
      const values = filter?.find((f) => f?.filterKey === "values")?.value;
      if (isNonEmptyVariable(values) && !isEmptyObject(values)) {
        // isNewJacket lets callers tell this apart from an actual existing-
        // jacket pick below — the typed search values are not a linked
        // jacket, so callers must not run field verification on them.
        if (onSaveClick) onSaveClick(values, { isNewJacket: true });
        if (onCloseClick) onCloseClick();
        if (onHide) onHide();
      } else {
        alert("Please search data first");
      }
    }
  };

  // If so, pass the data upward via onSaveClick.
  const handleSelectCurrentJacket = () => {
    if (!selectedFolder) {
      alert("Please select a folder from the list first.");
      return;
    }

    if (blockIfInJail && selectedFolder?.originalData?.inJail) {
      showDialog("info", {
        title: "Info",
        content:
          "The person associated with the selected Folder is already in jail. If this is the correct Folder, then you should not be creating a new Booking. Click the Cancel button, then click the Find button to search for the correct Booking to modify. Click on the flashing Folder flag for additional information",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (verifyAddressOnSelect) {
      const bio = selectedFolder?.originalData?.jackets?.bio?.[0];
      const address = buildCurrentPhysicalAddress(bio);
      if (address) {
        showDialog("confirm", {
          title: "Verify Current Physical Address",
          content: `Does ${buildFullName(bio)} still live at ${address}?`,
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          reverseButtonOrder: true,
          onConfirm: () => {
            if (onSaveClick) onSaveClick(selectedFolder, { isNewJacket: false });
          },
          onCancel: () => {
            if (onSaveClick) onSaveClick(selectedFolder, { isNewJacket: false, addressNotCurrent: true });
          },
        });
        return;
      }
    }

    if (onSaveClick) onSaveClick(selectedFolder, { isNewJacket: false });
  };

  // Callback to be passed to FolderFullDetails.
  // It receives the folder data when a row is clicked.
  const handleFolderSelected = (folder) => {
    setSelectedFolder(folder);
    if (isNonEmptyVariable(folder)) {
      setisDisabledCurrentJacket(() => false);
    }
  };

  return (
    <Modal
      designPattern="ant"
      show={show}
      header="Search for Folder"
      size="xxl"
      width="85%"
      onHide={handleModalHide}
      className="unstyle-padding"
      destroyOnClose
      footerActions={[
        <Button type="secondary" color="secondary" onClick={handleModalHide}>
          Close
        </Button>,
      ]}
    >
      <Spin spinning={irisLoading}>
        <StyledActions className="actions-container">
          <FolderDetailSearchActions
            onCancel={handleReset}
            onSearch={handleSearch}
            onSelectCurrentJacket={handleSelectCurrentJacket}
            onCreateNewJacket={onCreateNewJacket}
            isDisabledCurrentJacket={isDisabledCurrentJacket}
            onSearchIris={showIrisSearch && !hasSearched ? openIrisSearch : null}
            irisDeviceConnected={irisDeviceConnected}
          />
        </StyledActions>

        <FolderDetailSearchForm ref={searchFormRef} isFilterGrayBg={true} initialValues={initialValues} />

        <div style={{ padding: "20px" }}>
          <FolderFullDetails
            onFolderSelected={handleFolderSelected}
            selectedFolderTemp={selectedFolder}
            associates={true}
          />
        </div>
      </Spin>

      {irisSearchDetailsPopupOpen && (
        <SearchIrisDetailsPopup
          irisSearchDetailsPopupOpen={irisSearchDetailsPopupOpen}
          setisSearchIrisDetailsPopupOpen={setisSearchIrisDetailsPopupOpen}
          irisSearchData={irisSearchData}
          returnData={returnFromDetailsPopup}
        />
      )}
    </Modal>
  );
};

export default FolderDetailSearchModal;
